import { Callbacks, Client, Predict, type InputHandle, type Reconciler, type Room } from "@colyseus/sdk";
import {
  ArenaState,
  CHAT_MESSAGE,
  COMBAT_MESSAGE,
  LobbyMessage,
  NOTICE_MESSAGE,
  PROTOCOL_VERSION,
  PlayerInput,
  ROOM_NAME,
  SERVER_PATH,
  PropStatus,
  SIM_FIELDS,
  TrapPhase,
  liveArena,
  withBarricades,
  withTerrain,
  stepPlayer,
  type ArenaDefinition,
  type ChatLine,
  type CombatEvent,
  type Notice,
  type PickupState,
  type PlayerState,
  type ProjectileState,
  type RoomSettings,
} from "@arena/shared";
import { kitOf } from "@arena/character-data";
import { withMuseum } from "@arena/arena-data";
import { INTERPOLATION_DELAY_MS } from "../config.ts";

export interface SessionEvents {
  playerAdded(sessionId: string, player: PlayerState, isLocal: boolean): void;
  playerRemoved(sessionId: string): void;
  projectileAdded(id: string, projectile: ProjectileState): void;
  projectileRemoved(id: string): void;
  pickupAdded(id: string, pickup: PickupState): void;
  pickupRemoved(id: string): void;
  /** Authoritative combat event, for effects only (outcomes are in the state). */
  combat(event: CombatEvent): void;
  /** The server refused a lobby request, or has news for this player. */
  notice(notice: Notice): void;
  /** A line of chat from someone in the room (D49). */
  chat(line: ChatLine): void;
  dropped(): void;
  reconnected(): void;
  left(code: number): void;
}

/** Distance (m) above which a correction is a teleport (respawn) rather than an error to smooth. */
const TELEPORT_DISTANCE = 4;

/**
 * One connection to an arena room, with Colyseus' prediction stack wired in:
 * - the local fighter is predicted with the shared `stepPlayer` and
 *   reconciled against the server ack (rewind + replay, smoothed corrections);
 * - remote fighters are interpolated `INTERPOLATION_DELAY_MS` in the past.
 *
 * The prediction covers the local fighter's own movement, attacks and
 * abilities (all in `stepPlayer`, with the kit of the chosen champion).
 * Projectiles are the server's and are interpolated like remote fighters. Being hit, body pushes and the server granting the Super
 * Slam arrive with the state and are reconciled like any other correction.
 */
export class GameSession {
  readonly predict: Predict<ArenaState>;
  readonly input: InputHandle<PlayerInput>;
  readonly room: Room<unknown, ArenaState>;
  private arena: ArenaDefinition;
  /** Objects of the map that are solids: while broken, the floor has a gap there. */
  private blocking: ReadonlySet<string>;
  private readonly broken: string[] = [];
  private reconciler: Reconciler<PlayerState> | null = null;

  private constructor(room: Room<unknown, ArenaState>, arena: ArenaDefinition, events: SessionEvents) {
    this.room = room;
    this.arena = arena;
    this.blocking = new Set((arena.props ?? []).filter((p) => p.blocks).map((p) => p.id));
    this.predict = Predict.get(room, { mode: "lerp", delay: INTERPOLATION_DELAY_MS });
    this.input = room.input({ type: PlayerInput, mode: "reliable" });

    this.predict.attachAll("players", {
      x: { mode: "lerp", snap: TELEPORT_DISTANCE },
      y: { mode: "lerp", snap: TELEPORT_DISTANCE },
      z: { mode: "lerp", snap: TELEPORT_DISTANCE },
      fx: "lerp",
      fz: "lerp",
    });
    this.predict.attachAll("projectiles", { x: "lerp", y: "lerp", z: "lerp" });

    const callbacks = Callbacks.get(room);
    callbacks.onAdd("players", (player, sessionId) => {
      const isLocal = sessionId === room.sessionId;
      if (isLocal) {
        this.reconciler = this.predict.reconciler(player, {
          input: this.input,
          fields: SIM_FIELDS,
          // The champion only changes in the lobby; the kit is looked up at each step.
          // The arena as it is now: open hatches (D38) and broken objects (D39) are not there.
          step: (ctx, state, command) => stepPlayer(state, command, this.liveArena(), ctx.dt, kitOf(player.characterId,player.metamorphTicks)),
          snap: TELEPORT_DISTANCE,
        });
      }
      events.playerAdded(sessionId, player, isLocal);
    });
    callbacks.onRemove("players", (_player, sessionId) => events.playerRemoved(sessionId));
    callbacks.onAdd("projectiles", (projectile, id) => events.projectileAdded(id, projectile));
    callbacks.onRemove("projectiles", (_projectile, id) => events.projectileRemoved(id));
    callbacks.onAdd("pickups", (pickup, id) => events.pickupAdded(id, pickup));
    callbacks.onRemove("pickups", (_pickup, id) => events.pickupRemoved(id));
    room.onMessage(COMBAT_MESSAGE, (event: CombatEvent) => events.combat(event));
    room.onMessage(NOTICE_MESSAGE, (n: Notice) => events.notice(n));
    room.onMessage(CHAT_MESSAGE, (line: ChatLine) => events.chat(line));

    room.onDrop(() => events.dropped());
    room.onReconnect(() => events.reconnected());
    room.onLeave((code) => events.left(code));
  }

  /** The host picked another map (D49): the prediction walks on it. */
  setArena(arena: ArenaDefinition): void {
    this.arena = arena;
    this.blocking = new Set((arena.props ?? []).filter((p) => p.blocks).map((p) => p.id));
  }

  liveArena(): ArenaDefinition {
    this.broken.length = 0;
    if (this.blocking.size > 0) {
      this.room.state.props.forEach((p, id) => {
        if (p.status === PropStatus.Gone && this.blocking.has(id)) this.broken.push(id);
      });
    }
    // The museum's wing too, where the room waits between matches (D49).
    return withBarricades(withTerrain(liveArena(withMuseum(this.arena), this.room.state.trap === TrapPhase.Open, this.broken),this.room.state.terrain), this.room.state.barricades.values());
  }

  /** Creates a private room; the player becomes its host. */
  static async create(name: string, arena: ArenaDefinition, events: SessionEvents, settings: Partial<RoomSettings> = {}): Promise<GameSession> {
    const room = await new Client(serverEndpoint()).create(ROOM_NAME, {...joinOptions(name), settings}, ArenaState);
    return new GameSession(room, arena, events);
  }

  /** Joins a friend's room by its code. */
  static async joinByCode(code: string, name: string, arena: ArenaDefinition, events: SessionEvents): Promise<GameSession> {
    const room = await new Client(serverEndpoint()).joinById(code, joinOptions(name), ArenaState);
    return new GameSession(room, arena, events);
  }

  /** Takes back the same seat after a page reload (the server keeps it for a few seconds). */
  static async resume(token: string, arena: ArenaDefinition, events: SessionEvents): Promise<GameSession> {
    const room = await new Client(serverEndpoint()).reconnect(token, ArenaState);
    return new GameSession(room, arena, events);
  }

  get sessionId(): string {
    return this.room.sessionId;
  }

  get reconnectionToken(): string {
    return this.room.reconnectionToken;
  }

  get isHost(): boolean {
    return this.room.state.hostId === this.room.sessionId;
  }

  // Lobby requests: the server validates each one and answers with a notice if refused.
  chooseCharacter(characterId: string): void {
    this.room.send(LobbyMessage.Character, { characterId });
  }

  /** Into the arena through the museum's painting, or back to the museum (D49). */
  train(on: boolean): void {
    this.room.send(LobbyMessage.Training, { on });
  }

  /** Host only: the map, from the curator's register (D49). */
  chooseMap(arenaId: string): void {
    this.room.send(LobbyMessage.Map, { arenaId });
  }

  say(text: string): void {
    this.room.send(LobbyMessage.Chat, { text });
  }

  changeSettings(patch: Partial<RoomSettings>): void {
    this.room.send(LobbyMessage.Settings, patch);
  }

  startMatch(): void {
    this.room.send(LobbyMessage.Start);
  }

  backToLobby(): void {
    this.room.send(LobbyMessage.BackToLobby);
  }

  chooseTeam(team: number): void {
    this.room.send(LobbyMessage.Team, { team });
  }

  /** Host only: a sparring bot joins the room (training). Removed like a player, with `kick`. */
  addBot(): void {
    this.room.send(LobbyMessage.AddBot);
  }

  shuffleTeams(): void {
    this.room.send(LobbyMessage.Shuffle);
  }

  kick(sessionId: string): void {
    this.room.send(LobbyMessage.Kick, { sessionId });
  }

  get state(): ArenaState {
    return this.room.state;
  }

  /** Round-trip time in ms, smoothed. */
  get rtt(): number {
    return this.room.clock.smoothedRtt();
  }

  /** Inputs sent but not yet processed by the server. */
  get inputsInFlight(): number {
    return Math.max(0, this.input.sentCount - this.input.lastProcessed);
  }

  /** Exact predicted local state, for game logic (not smoothed). */
  get localPredicted(): PlayerState | null {
    return this.reconciler?.state ?? null;
  }

  /** Consented leave: the seat is freed at once. */
  async leave(): Promise<void> {
    this.room.removeAllListeners();
    await this.room.leave(true).catch(() => {});
  }
}

function joinOptions(name: string) {
  return { name, protocolVersion: PROTOCOL_VERSION };
}

export function serverEndpoint(): string {
  const override = import.meta.env.VITE_SERVER_URL as string | undefined;
  if (override) return override;
  return `${location.protocol}//${location.host}${SERVER_PATH}`;
}
